import { AmplifilesClient } from "./api-client";
import {
  CreateDfyDeliveryDto,
  CreateDfyOrderDto,
  CreateVideoFromDfyOrderResponseDto,
  DfyDeliveryDto,
  DfyOrderDto,
  DfyOrderListResponseDto,
  DfyOrderWithAssetsDto,
  GetDfyAssetMediaDownloadUrlResponseDto,
  GetDfyEditorOrdersQuery,
  GetDfyEditorVideoRendersQuery,
  PagedDfyEditorVideoRendersResponseDto,
  PagedDfyOrderResponseDto,
  SubmitDfyOrderDto,
  SubmitDfyOrderResponseDto,
  UpdateDfyDeliveryDto,
  UpdateDfyEditorOrderDto,
  UpdateDfyOrderDetailsDto,
} from "@repo/api/dfy";
import { DfyCouponDto } from "@repo/api/coupon";
import {
  GetSourceImageDownloadUrlResponseDto,
  SourceImageDto,
} from "@repo/api/source-image";

// Done-for-You is a proof-of-concept feature. All of its API access lives in this
// single file so the whole feature can be removed easily.
export class DfyApi {
  static async listCustomerOrders(teamExternalId: string) {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/dfy/orders`,
    );
    return DfyOrderListResponseDto.parse(response.data);
  }

  static async listEditorOrders(
    teamExternalId: string,
    query: GetDfyEditorOrdersQuery,
  ) {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/dfy/editor/orders`,
      { params: query },
    );
    return PagedDfyOrderResponseDto.parse(response.data);
  }

  static async getCoupon(teamExternalId: string, couponCode: string) {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/dfy/coupon/${encodeURIComponent(couponCode)}`,
    );
    return DfyCouponDto.parse(response.data);
  }

  static async createOrder(teamExternalId: string, data: CreateDfyOrderDto) {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/dfy/orders`,
      data,
    );
    return DfyOrderDto.parse(response.data);
  }

  static async getOrder(teamExternalId: string, orderExternalId: string) {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}`,
    );
    return DfyOrderWithAssetsDto.parse(response.data);
  }

  static async deleteOrder(teamExternalId: string, orderExternalId: string) {
    await AmplifilesClient.instance.delete(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}`,
    );
  }

  static async updateDetails(
    teamExternalId: string,
    orderExternalId: string,
    data: UpdateDfyOrderDetailsDto,
  ) {
    const response = await AmplifilesClient.instance.patch(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/details`,
      data,
    );
    return DfyOrderDto.parse(response.data);
  }

  static async uploadSourceImage(
    teamExternalId: string,
    orderExternalId: string,
    image: File,
  ) {
    const formData = new FormData();
    formData.append("image", image);

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/source-image`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return SourceImageDto.parse(response.data);
  }

  static async getSourceImageDownloadUrl(
    teamExternalId: string,
    orderExternalId: string,
    sourceImageExternalId: string,
  ) {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/source-image/${sourceImageExternalId}/download-url`,
    );
    return GetSourceImageDownloadUrlResponseDto.parse(response.data);
  }

  static async getAssetMediaDownloadUrl(
    teamExternalId: string,
    orderExternalId: string,
    assetMediaExternalId: string,
  ) {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/asset-media/${assetMediaExternalId}/download-url`,
    );
    return GetDfyAssetMediaDownloadUrlResponseDto.parse(response.data);
  }

  static async submitOrder(
    teamExternalId: string,
    orderExternalId: string,
    body: SubmitDfyOrderDto,
  ) {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/submit`,
      body,
    );
    return SubmitDfyOrderResponseDto.parse(response.data);
  }

  static async updateEditorOrder(
    teamExternalId: string,
    orderExternalId: string,
    data: UpdateDfyEditorOrderDto,
  ) {
    const response = await AmplifilesClient.instance.patch(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/editor`,
      data,
    );
    return DfyOrderDto.parse(response.data);
  }

  static async createVideo(teamExternalId: string, orderExternalId: string) {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/create-video`,
    );
    return CreateVideoFromDfyOrderResponseDto.parse(response.data);
  }

  static async listEditorVideoRenders(
    teamExternalId: string,
    orderExternalId: string,
    query: GetDfyEditorVideoRendersQuery,
  ) {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/editor/video-renders`,
      { params: query },
    );
    return PagedDfyEditorVideoRendersResponseDto.parse(response.data);
  }

  static async createDelivery(
    teamExternalId: string,
    orderExternalId: string,
    data: CreateDfyDeliveryDto,
  ) {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/dfy/orders/${orderExternalId}/deliveries`,
      data,
    );
    return DfyDeliveryDto.parse(response.data);
  }

  static async updateDelivery(
    teamExternalId: string,
    deliveryExternalId: string,
    data: UpdateDfyDeliveryDto,
  ) {
    const response = await AmplifilesClient.instance.patch(
      `/team/${teamExternalId}/dfy/deliveries/${deliveryExternalId}`,
      data,
    );
    return DfyDeliveryDto.parse(response.data);
  }

  static async deleteDelivery(
    teamExternalId: string,
    deliveryExternalId: string,
  ) {
    await AmplifilesClient.instance.delete(
      `/team/${teamExternalId}/dfy/deliveries/${deliveryExternalId}`,
    );
  }
}
